// Country-specific events calendar used by /events and /events/$country.
// Dates are the officially announced or historically fixed editions for the
// 2026–2027 season. Always re-check the official ticket page before booking —
// organisers move dates and on-sale windows every year.

export interface CountryEvent {
  /** url-safe id, unique within a country */
  id: string;
  name: string;
  category:
    | "Festival"
    | "Culture"
    | "Music"
    | "Sport"
    | "Business"
    | "Tech"
    | "Food & Drink"
    | "Community";
  /** ISO date (YYYY-MM-DD) — used for schema.org and sorting */
  startDate: string;
  /** ISO date; omit for single-day events */
  endDate?: string;
  /** human-readable date range shown in the UI */
  dateLabel: string;
  /** local start time / daily running hours */
  timeLabel: string;
  city: string;
  venue: string;
  /** street or area address for maps */
  address: string;
  priceLabel: string;
  /** official tickets, registration or RSVP page */
  ticketUrl: string;
  ticketLabel: string;
  /** true when entry is by free registration / RSVP rather than paid ticket */
  rsvp?: boolean;
  description: string;
  /** why it matters for expats, newcomers and remote workers */
  expatTip: string;
}

export interface CountryEventsData {
  /** matches MAP_COUNTRIES slug */
  slug: string;
  name: string;
  /** ISO-2 lowercase, for the round flag icon */
  code: string;
  /** true when the country has a full guide hub at /$country */
  hasGuide: boolean;
  currency: string;
  intro: string;
  planning: string;
  events: CountryEvent[];
}

const DATA: CountryEventsData[] = [
  // ---------------------------------------------------------------- USA ----
  {
    slug: "usa",
    name: "United States",
    code: "us",
    hasGuide: true,
    currency: "USD",
    intro:
      "American event culture runs on early booking. The biggest festivals, conferences and championship rounds sell out months ahead, and hotel prices in the host city often triple during the event week. If you have just arrived on an H-1B, F-1 or L-1 visa, these dates are also some of the cheapest professional networking you will find — badge prices look high until you compare them with a recruiter fee.",
    planning:
      "Plan around three practical constraints: domestic flights spike roughly six weeks out, most large venues in the US are now cashless and card-only, and any event requiring alcohol service will ask for a government photo ID — a foreign passport works, a photocopy does not. If you are on a non-immigrant visa, keep your I-94 and status documents at home in a safe place and travel with your passport plus a printed itinerary.",
    events: [
      {
        id: "sxsw",
        name: "SXSW (South by Southwest)",
        category: "Tech",
        startDate: "2027-03-12",
        endDate: "2027-03-18",
        dateLabel: "12–18 March 2027",
        timeLabel: "Sessions 9:30am–6pm daily; showcases run to 2am",
        city: "Austin, Texas",
        venue: "Austin Convention Center & citywide venues",
        address: "500 E Cesar Chavez St, Austin, TX 78701",
        priceLabel: "From about $1,095 for a Platinum badge; free outdoor events",
        ticketUrl: "https://www.sxsw.com/attend/",
        ticketLabel: "Buy SXSW badges",
        description: "One conference stapled to a music festival stapled to a film premiere circuit. Tech, media, health and creative tracks run in parallel across downtown Austin...",
        expatTip:
          "The best-value badge is usually the earliest tier released in the previous summer. Recruiters from most large US tech employers staff the trade show floor, which makes it useful if you are looking for H-1B-sponsoring employers.",
      },
      {
        id: "us-open-tennis",
        name: "US Open Tennis Championships",
        category: "Sport",
        startDate: "2026-08-24",
        endDate: "2026-09-13",
        dateLabel: "24 August – 13 September 2026",
        timeLabel: "Day sessions from 11am, night sessions from 7pm",
        city: "New York City",
        venue: "USTA Billie Jean King National Tennis Center",
        address: "Flushing Meadows Corona Park, Queens, NY 11368",
        priceLabel: "Grounds passes from about $85; Arthur Ashe sessions from about $150",
        ticketUrl: "https://www.usopen.org/en_US/tickets/",
        ticketLabel: "Buy US Open tickets",
        description:
          "The final Grand Slam of the tennis season, played on hard courts in Queens with the loudest crowds on the tour.",
        expatTip:
          "Qualifying week before the main draw is free to attend and is genuinely good tennis — an easy first outing if you have just moved to New York and want a low-cost day out.",
      },
      {
        id: "coachella",
        name: "Coachella Valley Music and Arts Festival",
        category: "Music",
        startDate: "2027-04-09",
        endDate: "2027-04-18",
        dateLabel: "Two weekends, 9–18 April 2027",
        timeLabel: "Gates 11am, music until midnight",
        city: "Indio, California",
        venue: "Empire Polo Club",
        address: "81-800 Avenue 51, Indio, CA 92201",
        priceLabel: "General admission from about $499 plus fees; payment plans available",
        ticketUrl: "https://www.coachella.com/passes",
        ticketLabel: "Register for Coachella passes",
        description:
          "The desert festival that sets the tone for the American festival season, with large-scale art installations alongside the headline stages.",
        expatTip:
          "Advance sale registration opens the previous summer and sells through in minutes. Shuttle passes are far cheaper and less stressful than parking or ride-hailing in the desert.",
      },
      {
        id: "macys-thanksgiving-parade",
        name: "Macy's Thanksgiving Day Parade",
        category: "Culture",
        startDate: "2026-11-26",
        dateLabel: "Thursday 26 November 2026",
        timeLabel: "8:30am–12pm",
        city: "New York City",
        venue: "Central Park West to Herald Square",
        address: "Parade route from W 77th St to Macy's, 151 W 34th St, New York, NY",
        priceLabel: "Free to watch from the public route",
        ticketUrl: "https://www.macys.com/social/parade/",
        ticketLabel: "See the parade route and RSVP info",
        rsvp: true,
        description:
          "Giant character balloons, marching bands and floats travelling down Manhattan on Thanksgiving morning.",
        expatTip:
          "Arrive by 6:30am for a usable spot on the Upper West Side stretch. The balloon inflation the evening before is quieter, free and much easier with children.",
      },
      {
        id: "new-year-times-square",
        name: "Times Square New Year's Eve Ball Drop",
        category: "Community",
        startDate: "2026-12-31",
        dateLabel: "Thursday 31 December 2026",
        timeLabel: "Viewing areas open from 3pm; ball drops at midnight",
        city: "New York City",
        venue: "Times Square",
        address: "Broadway & 7th Ave, New York, NY 10036",
        priceLabel: "Free in the public pens; ticketed hotel and rooftop views vary",
        ticketUrl: "https://www.timessquarenyc.org/times-square-new-years-eve",
        ticketLabel: "Plan your New Year's Eve visit",
        rsvp: true,
        description:
          "The most-watched countdown in the world, with performances through the evening from the Times Square stages.",
        expatTip:
          "There are no bathrooms and no re-entry once you are inside a viewing pen. Most newcomers enjoy a neighbourhood bar countdown far more — book a table by early December.",
      },
    ],
  },

  // ----------------------------------------------------------------- UK ----
  {
    slug: "uk",
    name: "United Kingdom",
    code: "gb",
    hasGuide: true,
    currency: "GBP",
    intro:
      "The British events calendar is built around ballots and pre-registration rather than pure first-come sales. Wimbledon, Glastonbury and the big London runs all use registration windows that close months before the event itself, so the practical skill is diarising the sign-up date rather than the event date.",
    planning:
      "Rail strikes and engineering works are the biggest risk to a UK event plan — check National Rail the week before, and buy advance tickets rather than turning up. A 16–25 or Two Together Railcard pays for itself in two trips, and most large venues now require digital tickets held in the official app rather than printed PDFs.",
    events: [
      {
        id: "wimbledon",
        name: "The Championships, Wimbledon",
        category: "Sport",
        startDate: "2027-06-28",
        endDate: "2027-07-11",
        dateLabel: "28 June – 11 July 2027",
        timeLabel: "Grounds open 10am; play from 11am on outside courts",
        city: "London",
        venue: "All England Lawn Tennis Club",
        address: "Church Rd, Wimbledon, London SW19 5AE",
        priceLabel: "Ballot tickets from about £30; Centre Court finals considerably higher",
        ticketUrl: "https://www.wimbledon.com/en_GB/atoz/tickets.html",
        ticketLabel: "Enter the Wimbledon ballot",
        description:
          "The oldest tennis tournament in the world, played on grass, with strict all-white player dress and a queue tradition that is an event in itself.",
        expatTip:
          "The public ballot opens the previous autumn and is free to enter. If you miss it, the physical Queue still releases grounds passes each morning — arrive before 6am.",
      },
      {
        id: "edinburgh-fringe",
        name: "Edinburgh Festival Fringe",
        category: "Festival",
        startDate: "2026-08-07",
        endDate: "2026-08-31",
        dateLabel: "7–31 August 2026",
        timeLabel: "Shows from 10am to past midnight",
        city: "Edinburgh",
        venue: "300+ venues across the city",
        address: "Fringe Box Office, 180 High St, Edinburgh EH1 1QS",
        priceLabel: "Many free shows; ticketed shows typically £10–£20",
        ticketUrl: "https://www.edfringe.com/",
        ticketLabel: "Browse Fringe tickets",
        description:
          "The largest performing arts festival on earth: comedy, theatre, cabaret and spoken word in every available room in Edinburgh for most of August.",
        expatTip:
          "Accommodation in Edinburgh in August costs three to four times the normal rate. Book by February, or stay in Glasgow and take the 50-minute train.",
      },
      {
        id: "notting-hill-carnival",
        name: "Notting Hill Carnival",
        category: "Festival",
        startDate: "2026-08-30",
        endDate: "2026-08-31",
        dateLabel: "Sunday 30 – Monday 31 August 2026",
        timeLabel: "Parade 10am–7pm; Sunday is family day",
        city: "London",
        venue: "Streets of Notting Hill and Ladbroke Grove",
        address: "Great Western Rd to Ladbroke Grove, London W11",
        priceLabel: "Free to attend",
        ticketUrl: "https://en.wikipedia.org/wiki/Notting_Hill_Carnival",
        ticketLabel: "Carnival route and RSVP info",
        rsvp: true,
        description:
          "Europe's biggest street festival, rooted in London's Caribbean communities, with sound systems, mas bands and street food along the whole route.",
        expatTip:
          "Several Underground stations become exit-only. Save the official route map offline and agree a meeting point in advance — mobile networks struggle under the crowd load.",
      },
      {
        id: "london-marathon",
        name: "TCS London Marathon",
        category: "Sport",
        startDate: "2027-04-25",
        dateLabel: "Sunday 25 April 2027",
        timeLabel: "First wave 9am; course closes around 5:30pm",
        city: "London",
        venue: "Greenwich Park to The Mall",
        address: "Start: Blackheath, London SE3; Finish: The Mall, SW1A",
        priceLabel: "Ballot entry about £75 (UK residents); free to spectate",
        ticketUrl: "https://www.tcslondonmarathon.com/enter/ballot",
        ticketLabel: "Enter the marathon ballot",
        description:
          "One of the six World Marathon Majors, run past Tower Bridge, Canary Wharf and Buckingham Palace with roughly 750,000 spectators along the route.",
        expatTip:
          "The ballot opens for a short window straight after each year's race. Charity places remain available later but come with a fundraising minimum, usually £2,000 or more.",
      },
      {
        id: "hogmanay",
        name: "Edinburgh's Hogmanay",
        category: "Community",
        startDate: "2026-12-30",
        endDate: "2027-01-01",
        dateLabel: "30 December 2026 – 1 January 2027",
        timeLabel: "Street party from 7pm; fireworks at midnight",
        city: "Edinburgh",
        venue: "Princes Street & Edinburgh Castle backdrop",
        address: "Princes St, Edinburgh EH2",
        priceLabel: "Street Party tickets from about £30",
        ticketUrl: "https://www.edinburghshogmanay.com/",
        ticketLabel: "Buy Hogmanay tickets",
        description:
          "Scotland's three-day New Year celebration, ending with a torchlight procession, a ticketed street party and fireworks over the castle.",
        expatTip:
          "The 1 January Loony Dook and the Ceilidh under the Castle are the best-value tickets and sell out earlier than the main street party.",
      },
    ],
  },

  // ---------------------------------------------------------- AUSTRALIA ----
  {
    slug: "australia",
    name: "Australia",
    code: "au",
    hasGuide: true,
    currency: "AUD",
    intro:
      "Australia's calendar is inverted for anyone arriving from the northern hemisphere: the biggest sport and festival season runs from December through March, in the middle of summer. Public holidays cluster around those events, which makes them the natural time to take leave rather than in July.",
    planning:
      "Distances are the planning problem. A Sydney-to-Perth flight is five hours, and domestic fares spike hard around the Australian Open and Melbourne Cup. Book flights ten to twelve weeks out, plan for genuinely strong sun exposure at outdoor events, and note that most venues are cashless with tap-to-pay only.",
    events: [
      {
        id: "australian-open",
        name: "Australian Open",
        category: "Sport",
        startDate: "2027-01-18",
        endDate: "2027-01-31",
        dateLabel: "18–31 January 2027",
        timeLabel: "Day sessions from 11am, night sessions from 7pm",
        city: "Melbourne",
        venue: "Melbourne Park",
        address: "Olympic Blvd, Melbourne VIC 3000",
        priceLabel: "Ground passes from about A$59; arena sessions from about A$100",
        ticketUrl: "https://ausopen.com/tickets",
        ticketLabel: "Buy Australian Open tickets",
        description:
          "The first Grand Slam of the year, played in the heart of Melbourne with the roofs closed when the heat policy is triggered.",
        expatTip:
          "A ground pass in the first week gives you access to a dozen courts and is one of the best-value sporting tickets in the country. Take the free City Circle tram to Federation Square and walk over.",
      },
      {
        id: "vivid-sydney",
        name: "Vivid Sydney",
        category: "Culture",
        startDate: "2027-05-21",
        endDate: "2027-06-12",
        dateLabel: "21 May – 12 June 2027",
        timeLabel: "Light walk 6pm–11pm nightly",
        city: "Sydney",
        venue: "Circular Quay, Opera House, Darling Harbour",
        address: "Circular Quay, Sydney NSW 2000",
        priceLabel: "Light walk free; talks and music sessions ticketed",
        ticketUrl: "https://www.vividsydney.com/",
        ticketLabel: "Plan Vivid and book sessions",
        rsvp: true,
        description:
          "A three-week festival of light projections, ideas talks and music that turns the harbour foreshore into an outdoor gallery every evening.",
        expatTip:
          "Go on a weeknight after 8:30pm to avoid the crush. The Ideas programme is genuinely strong and cheaper than most conferences.",
      },
      {
        id: "melbourne-cup",
        name: "Melbourne Cup Carnival",
        category: "Sport",
        startDate: "2026-10-31",
        endDate: "2026-11-07",
        dateLabel: "31 October – 7 November 2026 (Cup Day 3 November)",
        timeLabel: "Gates 10:30am; Cup race about 3pm",
        city: "Melbourne",
        venue: "Flemington Racecourse",
        address: "448 Epsom Rd, Flemington VIC 3031",
        priceLabel: "General admission from about A$85",
        ticketUrl: "https://www.vrc.com.au/",
        ticketLabel: "Buy Carnival tickets",
        description:
          "Four days of racing built around the Melbourne Cup, a public holiday in Victoria and the most-watched race in the southern hemisphere.",
        expatTip:
          "Cup Day is a Victorian public holiday — confirm with your employer whether it applies to your contract before booking. Dress codes in the reserved enclosures are enforced.",
      },
      {
        id: "sydney-nye",
        name: "Sydney New Year's Eve Fireworks",
        category: "Community",
        startDate: "2026-12-31",
        dateLabel: "Thursday 31 December 2026",
        timeLabel: "Family fireworks 9pm; midnight display at 12am",
        city: "Sydney",
        venue: "Sydney Harbour foreshore",
        address: "Sydney Harbour, Sydney NSW",
        priceLabel: "Free public vantage points; ticketed vantage sites vary",
        ticketUrl: "https://www.sydneynewyearseve.com/",
        ticketLabel: "Vantage points and ticket info",
        rsvp: true,
        description:
          "One of the first major midnight celebrations on earth each year, staged across the Harbour Bridge and multiple barges.",
        expatTip:
          "Free vantage points in the Botanic Garden and Bradfield Park fill by early afternoon. Ticketed sites are worth it if you want a guaranteed seat and toilets.",
      },
    ],
  },

  // -------------------------------------------------------------- ITALY ----
  {
    slug: "italy",
    name: "Italy",
    code: "it",
    hasGuide: true,
    currency: "EUR",
    intro:
      "Italian events are civic first and commercial second. Carnevale, the Palio and the patron-saint festivals belong to their cities and neighbourhoods, which is exactly why they are the fastest way for a newcomer to meet people outside the expat bubble.",
    planning:
      "August is the national holiday month — many businesses close around Ferragosto on 15 August, and coastal prices peak. Trains are the sensible way to move between event cities: Frecciarossa fares are cheapest booked two to three months ahead, and regional trains require validating the ticket before boarding.",
    events: [
      {
        id: "venice-carnival",
        name: "Carnevale di Venezia",
        category: "Festival",
        startDate: "2027-01-30",
        endDate: "2027-02-09",
        dateLabel: "30 January – 9 February 2027",
        timeLabel: "Street events daily; masked balls from 8pm",
        city: "Venice",
        venue: "Piazza San Marco & citywide",
        address: "Piazza San Marco, 30124 Venezia VE",
        priceLabel: "Street events free; masked balls from about €200",
        ticketUrl: "https://www.carnevale.venezia.it/en/",
        ticketLabel: "Carnival programme and tickets",
        description:
          "Ten days of masked processions, water parades along the Grand Canal and formal balls in historic palazzi.",
        expatTip:
          "Stay in Mestre or Padua and commute in — Venice hotel rates during Carnevale are extreme. The Volo dell'Angelo in San Marco is free but requires arriving two hours early.",
      },
      {
        id: "palio-di-siena",
        name: "Palio di Siena",
        category: "Culture",
        startDate: "2027-07-02",
        dateLabel: "2 July 2027 (second running 16 August 2027)",
        timeLabel: "Pageantry from 5pm; race about 7:30pm",
        city: "Siena",
        venue: "Piazza del Campo",
        address: "Piazza del Campo, 53100 Siena SI",
        priceLabel: "Free standing in the centre of the piazza; balcony seats from about €250",
        ticketUrl: "https://www.comune.siena.it/",
        ticketLabel: "Palio info and seat enquiries",
        rsvp: true,
        description:
          "A bareback horse race between Siena's historic contrade, run three laps around the medieval square in about ninety seconds.",
        expatTip:
          "Standing in the piazza is free but you are locked in for hours with no shade or exit. Balcony seats are sold privately by the building owners and should be arranged months ahead.",
      },
      {
        id: "milan-design-week",
        name: "Milan Design Week / Salone del Mobile",
        category: "Business",
        startDate: "2027-04-13",
        endDate: "2027-04-18",
        dateLabel: "13–18 April 2027",
        timeLabel: "Fairgrounds 9:30am–6:30pm; city installations to 9pm",
        city: "Milan",
        venue: "Fiera Milano Rho & Fuorisalone districts",
        address: "Strada Statale del Sempione 28, 20017 Rho MI",
        priceLabel: "Trade tickets from about €40; many Fuorisalone events free",
        ticketUrl: "https://www.salonemilano.it/en",
        ticketLabel: "Register for Salone del Mobile",
        description:
          "The world's largest furniture and design fair, plus the Fuorisalone programme that takes over Brera, Tortona and Isola with free installations.",
        expatTip:
          "If you work in design, architecture or product, the Fuorisalone side events are the real networking value and most are free with online RSVP.",
      },
      {
        id: "venice-film-festival",
        name: "Venice International Film Festival",
        category: "Culture",
        startDate: "2026-09-02",
        endDate: "2026-09-12",
        dateLabel: "2–12 September 2026",
        timeLabel: "Screenings from 9am to late evening",
        city: "Venice (Lido)",
        venue: "Palazzo del Cinema, Lido di Venezia",
        address: "Lungomare Guglielmo Marconi, 30126 Lido VE",
        priceLabel: "Single screenings from about €20; season passes higher",
        ticketUrl: "https://www.labiennale.org/en/cinema",
        ticketLabel: "Buy Biennale Cinema tickets",
        description:
          "The oldest film festival in the world and the traditional launchpad for the autumn awards season.",
        expatTip:
          "Public tickets are released online roughly two weeks before the festival. Under-26 accreditation is heavily discounted and worth checking if you are a student in Italy.",
      },
    ],
  },

  // ----------------------------------------------------------- PORTUGAL ----
  {
    slug: "portugal",
    name: "Portugal",
    code: "pt",
    hasGuide: true,
    currency: "EUR",
    intro:
      "Portugal packs an unusual amount into a small country: a top-tier tech conference, some of Europe's biggest music festivals and street celebrations that still feel local. For D7 and D8 visa holders it is also the cheapest place in Western Europe to build a professional network from scratch.",
    planning:
      "Lisbon and Porto accommodation prices roughly double during Web Summit and the June festivals, so book by spring. Comboios de Portugal intercity trains are inexpensive but sell out on festival weekends; the Lisbon–Porto Alfa Pendular is the fastest link and should be booked online in advance.",
    events: [
      {
        id: "web-summit",
        name: "Web Summit Lisbon",
        category: "Tech",
        startDate: "2026-11-09",
        endDate: "2026-11-12",
        dateLabel: "9–12 November 2026",
        timeLabel: "Doors 9am; Night Summit events from 8pm",
        city: "Lisbon",
        venue: "MEO Arena & FIL, Parque das Nações",
        address: "Rossio dos Olivais, 1990-231 Lisboa",
        priceLabel: "General attendee tickets from about €395",
        ticketUrl: "https://websummit.com/tickets/",
        ticketLabel: "Buy Web Summit tickets",
        description:
          "One of the largest technology conferences in the world, with startup alley, investor tracks and four nights of city-wide side events.",
        expatTip:
          "Night Summit is where hiring conversations actually happen and is included with any badge. Startups with under three years of trading can apply for heavily discounted exhibition packages.",
      },
      {
        id: "santos-populares",
        name: "Festas de Lisboa / Santo António",
        category: "Festival",
        startDate: "2027-06-12",
        endDate: "2027-06-13",
        dateLabel: "12–13 June 2027 (programme runs all June)",
        timeLabel: "Street parties from 8pm to sunrise",
        city: "Lisbon",
        venue: "Alfama, Graça, Bica and Mouraria districts",
        address: "Alfama, 1100 Lisboa",
        priceLabel: "Free; food and drink bought from neighbourhood stalls",
        ticketUrl: "https://www.festasdelisboa.com/",
        ticketLabel: "Festas de Lisboa programme",
        rsvp: true,
        description: "Lisbon's patron saint festival: grilled sardines, bunting across every alley, marchas populares parading down Avenida da Liberdade and neighbourhood parties ...",
        expatTip:
          "This is the single best night of the year to meet Portuguese neighbours rather than other newcomers. Bring cash — many of the street stalls still do not take cards.",
      },
      {
        id: "nos-alive",
        name: "NOS Alive",
        category: "Music",
        startDate: "2027-07-08",
        endDate: "2027-07-10",
        dateLabel: "8–10 July 2027",
        timeLabel: "Gates 4pm; music until 4am",
        city: "Oeiras (Greater Lisbon)",
        venue: "Passeio Marítimo de Algés",
        address: "Passeio Marítimo de Algés, 1495-165 Algés",
        priceLabel: "Day tickets from about €79; three-day passes from about €175",
        ticketUrl: "https://nosalive.com/en/",
        ticketLabel: "Buy NOS Alive tickets",
        description:
          "A riverside festival with international headliners across six stages, twenty minutes by train from central Lisbon.",
        expatTip:
          "The Cascais line train from Cais do Sodré runs late on festival nights and is far quicker than a taxi. Passes are cheapest in the first-release tier the previous autumn.",
      },
      {
        id: "sao-joao-porto",
        name: "Festa de São João do Porto",
        category: "Community",
        startDate: "2027-06-23",
        endDate: "2027-06-24",
        dateLabel: "Night of 23–24 June 2027",
        timeLabel: "From 6pm to sunrise; fireworks at midnight",
        city: "Porto",
        venue: "Ribeira & the Douro riverfront",
        address: "Cais da Ribeira, 4050-510 Porto",
        priceLabel: "Free",
        ticketUrl: "https://www.visitporto.travel/",
        ticketLabel: "Porto São João visitor info",
        rsvp: true,
        description:
          "Porto's midsummer festival — plastic hammers, grilled sardines, paper balloons and midnight fireworks over the Douro.",
        expatTip:
          "Trains and metro run through the night. Book accommodation by March; this weekend is busier in Porto than New Year.",
      },
    ],
  },

  // ------------------------------------------------------------- CANADA ----
  {
    slug: "canada",
    name: "Canada",
    code: "ca",
    hasGuide: true,
    currency: "CAD",
    intro:
      "Canada's events split cleanly into a short, intense summer festival season and a winter programme built around snow. For new permanent residents and study-permit holders, the summer city festivals are also where most volunteer and casual work opportunities appear.",
    planning:
      "Book flights and hotels early for Calgary Stampede and TIFF — both fill their host cities. Winter events need real layering rather than a single heavy coat, and provincial holiday calendars differ, so check whether a long weekend applies in your province before planning travel.",
    events: [
      {
        id: "tiff",
        name: "Toronto International Film Festival (TIFF)",
        category: "Culture",
        startDate: "2026-09-10",
        endDate: "2026-09-20",
        dateLabel: "10–20 September 2026",
        timeLabel: "Screenings from 9am to midnight",
        city: "Toronto",
        venue: "TIFF Lightbox & King Street West venues",
        address: "350 King St W, Toronto, ON M5V 3X5",
        priceLabel: "Single tickets from about C$29; ticket packages cheaper per film",
        ticketUrl: "https://tiff.net/",
        ticketLabel: "Buy TIFF tickets",
        description:
          "A major stop on the international awards circuit, with public screenings, red carpets and industry conference days.",
        expatTip:
          "Ticket packages go on sale weeks before individual tickets and are the only reliable way to see the high-demand premieres. Rush lines release seats an hour before each screening.",
      },
      {
        id: "calgary-stampede",
        name: "Calgary Stampede",
        category: "Festival",
        startDate: "2027-07-02",
        endDate: "2027-07-11",
        dateLabel: "2–11 July 2027",
        timeLabel: "Grounds 11am–midnight; rodeo from 1:15pm",
        city: "Calgary",
        venue: "Stampede Park",
        address: "1410 Olympic Way SE, Calgary, AB T2G 2W1",
        priceLabel: "Gate admission from about C$25; rodeo and Evening Show extra",
        ticketUrl: "https://www.calgarystampede.com/",
        ticketLabel: "Buy Stampede tickets",
        description:
          "Ten days of rodeo, chuckwagon racing, midway rides and free community pancake breakfasts across the city.",
        expatTip:
          "The free pancake breakfasts hosted by neighbourhoods and employers are a genuinely easy way to meet people if you have just landed in Alberta.",
      },
      {
        id: "montreal-jazz",
        name: "Festival International de Jazz de Montréal",
        category: "Music",
        startDate: "2027-06-24",
        endDate: "2027-07-04",
        dateLabel: "24 June – 4 July 2027",
        timeLabel: "Outdoor stages from 5pm; indoor concerts from 6pm",
        city: "Montréal",
        venue: "Quartier des Spectacles",
        address: "Place des Arts, 175 Rue Sainte-Catherine O, Montréal, QC",
        priceLabel: "Hundreds of free outdoor shows; indoor concerts from about C$45",
        ticketUrl: "https://www.montrealjazzfest.com/en",
        ticketLabel: "Browse Jazz Fest tickets",
        description:
          "The largest jazz festival in the world, with most of the programme free and outdoors in downtown Montréal.",
        expatTip:
          "Because so much is free, this is the cheapest big-city cultural week in Canada. Combine it with Saint-Jean-Baptiste on 24 June, a Québec public holiday.",
      },
      {
        id: "winterlude",
        name: "Winterlude (Bal de Neige)",
        category: "Community",
        startDate: "2027-02-05",
        endDate: "2027-02-21",
        dateLabel: "5–21 February 2027, weekends",
        timeLabel: "Sites open 9am–9pm",
        city: "Ottawa–Gatineau",
        venue: "Rideau Canal Skateway & Jacques-Cartier Park",
        address: "Rideau Canal, Ottawa, ON K1N",
        priceLabel: "Free; skate rental about C$25 per day",
        ticketUrl: "https://www.canada.ca/en/canadian-heritage/campaigns/winterlude.html",
        ticketLabel: "Winterlude programme and RSVP",
        rsvp: true,
        description:
          "Canada's flagship winter festival: ice sculpture competitions, snow playgrounds and skating on the world's largest naturally frozen rink.",
        expatTip:
          "The Skateway only opens when ice thickness allows — check the daily conditions page before travelling. Dress in three layers and bring proper mittens rather than gloves.",
      },
    ],
  },

  // ------------------------------------------------------------ GERMANY ----
  {
    slug: "germany",
    name: "Germany",
    code: "de",
    hasGuide: false,
    currency: "EUR",
    intro:
      "German events run to schedule and reward advance booking. Between Oktoberfest, the film and trade-fair calendar and hundreds of Christmas markets, there is a well-organised reason to travel almost every month.",
    planning:
      "Deutsche Bahn Sparpreis fares open six months ahead and are the cheapest way to move between event cities. Many venues and market stalls still prefer cash or Girocard over international credit cards, so carry euros.",
    events: [
      {
        id: "oktoberfest",
        name: "Oktoberfest",
        category: "Festival",
        startDate: "2026-09-19",
        endDate: "2026-10-04",
        dateLabel: "19 September – 4 October 2026",
        timeLabel: "Tents 10am–11:30pm (from 9am weekends)",
        city: "Munich",
        venue: "Theresienwiese",
        address: "Theresienwiese, 80336 München",
        priceLabel: "Free entry; table reservations and steins charged separately",
        ticketUrl: "https://en.wikipedia.org/wiki/Oktoberfest",
        ticketLabel: "Table reservations and info",
        rsvp: true,
        description:
          "The world's largest folk festival: fourteen large beer tents, funfair rides and traditional Bavarian music for just over two weeks.",
        expatTip:
          "Entry is free — you only pay for what you drink and eat. Table reservations open in the spring and are essential for evenings and weekends.",
      },
      {
        id: "berlinale",
        name: "Berlinale (Berlin International Film Festival)",
        category: "Culture",
        startDate: "2027-02-11",
        endDate: "2027-02-21",
        dateLabel: "11–21 February 2027",
        timeLabel: "Screenings from 9am to midnight",
        city: "Berlin",
        venue: "Berlinale Palast & Potsdamer Platz cinemas",
        address: "Marlene-Dietrich-Platz 1, 10785 Berlin",
        priceLabel: "Public tickets from about €16",
        ticketUrl: "https://www.berlinale.de/en/home.html",
        ticketLabel: "Buy Berlinale tickets",
        description:
          "One of Europe's big three film festivals and the most public-facing — a large share of seats are sold to ordinary ticket buyers.",
        expatTip:
          "Tickets go on sale three days before each screening and the online queue moves quickly. Weekday morning screenings are the easiest to get.",
      },
      {
        id: "christmas-markets-de",
        name: "German Christmas Markets",
        category: "Community",
        startDate: "2026-11-23",
        endDate: "2026-12-24",
        dateLabel: "23 November – 24 December 2026",
        timeLabel: "Typically 10am–9pm daily",
        city: "Nationwide (Nuremberg, Dresden, Cologne)",
        venue: "City squares and old towns",
        address: "Hauptmarkt, 90403 Nürnberg (Christkindlesmarkt)",
        priceLabel: "Free entry; mug deposit typically €3–5",
        ticketUrl: "https://www.germany.travel/en/home.html",
        ticketLabel: "Find markets and opening dates",
        rsvp: true,
        description:
          "Hundreds of Weihnachtsmärkte selling Glühwein, roasted nuts and handmade decorations from late November until Christmas Eve.",
        expatTip:
          "Your Glühwein mug deposit is refundable — or keep the mug as the cheapest souvenir in Germany. Weekday evenings are far less crowded than weekends.",
      },
      {
        id: "karneval-koeln",
        name: "Cologne Carnival (Karneval)",
        category: "Festival",
        startDate: "2027-02-04",
        endDate: "2027-02-09",
        dateLabel: "4–9 February 2027",
        timeLabel: "Weiberfastnacht from 11:11am; Rose Monday parade from 10am",
        city: "Cologne",
        venue: "Altstadt and the Rose Monday parade route",
        address: "Alter Markt, 50667 Köln",
        priceLabel: "Street events free; ball tickets vary",
        ticketUrl: "https://www.koelnerkarneval.de/",
        ticketLabel: "Carnival programme and tickets",
        rsvp: true,
        description:
          "Germany's 'fifth season' peaks with six days of costumes, street singing and the enormous Rosenmontag parade.",
        expatTip:
          "Many offices in the Rhineland close or finish early on Weiberfastnacht and Rose Monday, even though neither is a formal public holiday. Check your contract.",
      },
    ],
  },

  // ------------------------------------------------------------- FRANCE ----
  {
    slug: "france",
    name: "France",
    code: "fr",
    hasGuide: false,
    currency: "EUR",
    intro:
      "France schedules its cultural life around a small number of national moments — the Fête de la Musique, Bastille Day, the Tour de France — plus a dense professional calendar in Paris, Cannes and Lyon.",
    planning:
      "TGV tickets open three to four months ahead at the lowest fares. Expect reduced service and closures around 14 July and through August, and note that most French events require an ID check at bag control for large gatherings.",
    events: [
      {
        id: "fete-de-la-musique",
        name: "Fête de la Musique",
        category: "Music",
        startDate: "2027-06-21",
        dateLabel: "Monday 21 June 2027",
        timeLabel: "Afternoon until around 1am",
        city: "Nationwide (Paris, Lyon, Marseille)",
        venue: "Streets, squares and courtyards across France",
        address: "Citywide, Paris and every French town",
        priceLabel: "Free",
        ticketUrl: "https://fetedelamusique.culture.gouv.fr/",
        ticketLabel: "Find events near you",
        rsvp: true,
        description:
          "On the summer solstice, anyone can play music in public anywhere in France — from conservatory quartets to garage bands on street corners.",
        expatTip:
          "The best experience is walking one arrondissement slowly rather than chasing a lineup. Métro service is extended in Paris on the night.",
      },
      {
        id: "bastille-day",
        name: "Bastille Day (Fête Nationale)",
        category: "Community",
        startDate: "2027-07-14",
        dateLabel: "Wednesday 14 July 2027",
        timeLabel: "Parade from 10am; fireworks at 11pm",
        city: "Paris",
        venue: "Champs-Élysées & Champ de Mars",
        address: "Champ de Mars, 75007 Paris",
        priceLabel: "Free",
        ticketUrl: "https://www.paris.fr/",
        ticketLabel: "Official city programme",
        rsvp: true,
        description:
          "The national day: a military parade down the Champs-Élysées in the morning and fireworks from the Eiffel Tower at night.",
        expatTip:
          "The firemen's balls (bals des pompiers) on the 13th and 14th are held in fire stations across Paris, cost a few euros and are far more fun than the crowds at the Champ de Mars.",
      },
      {
        id: "cannes-film-festival",
        name: "Festival de Cannes",
        category: "Culture",
        startDate: "2027-05-11",
        endDate: "2027-05-22",
        dateLabel: "11–22 May 2027",
        timeLabel: "Screenings from 8:30am to midnight",
        city: "Cannes",
        venue: "Palais des Festivals et des Congrès",
        address: "1 Bd de la Croisette, 06400 Cannes",
        priceLabel: "Industry accreditation required; Cinéma de la Plage free",
        ticketUrl: "https://www.festival-cannes.com/en/",
        ticketLabel: "Accreditation and programme",
        description:
          "The most prestigious film festival in the world, largely accredited-only, with a parallel market that is the industry's biggest dealmaking week.",
        expatTip:
          "Cinéma de la Plage screens festival films free on the beach each evening with no accreditation needed — the only realistic public access.",
      },
      {
        id: "tour-de-france",
        name: "Tour de France",
        category: "Sport",
        startDate: "2027-07-03",
        endDate: "2027-07-25",
        dateLabel: "3–25 July 2027",
        timeLabel: "Stages typically 11am–5:30pm",
        city: "Nationwide, finishing in Paris",
        venue: "Public roads; final stage on the Champs-Élysées",
        address: "Av. des Champs-Élysées, 75008 Paris",
        priceLabel: "Free at the roadside; Paris finish grandstands ticketed",
        ticketUrl: "https://www.letour.fr/en/",
        ticketLabel: "Route map and hospitality",
        rsvp: true,
        description:
          "Three weeks of racing across France, free to watch from almost the entire route including the mountain climbs.",
        expatTip:
          "The publicity caravan passes about two hours before the riders and throws out samples — that is when families should arrive, not at race time.",
      },
    ],
  },

  // -------------------------------------------------------------- SPAIN ----
  {
    slug: "spain",
    name: "Spain",
    code: "es",
    hasGuide: false,
    currency: "EUR",
    intro:
      "Spanish celebrations are regional, loud and deeply local. Each autonomous community keeps its own holiday calendar, so the event you can attend depends heavily on which city you settle in.",
    planning:
      "Renfe AVE tickets are cheapest around two months out. Summer events start late — a 'night' event genuinely means midnight onwards — and August in inland cities like Seville and Madrid is extremely hot.",
    events: [
      {
        id: "las-fallas",
        name: "Las Fallas de València",
        category: "Festival",
        startDate: "2027-03-15",
        endDate: "2027-03-19",
        dateLabel: "15–19 March 2027",
        timeLabel: "Mascletà daily at 2pm; La Cremà on the 19th at midnight",
        city: "Valencia",
        venue: "Citywide, centred on Plaça de l'Ajuntament",
        address: "Plaça de l'Ajuntament, 46002 València",
        priceLabel: "Free; grandstand seats for the parade ticketed",
        ticketUrl: "https://www.fallas.com/",
        ticketLabel: "Fallas programme and seats",
        rsvp: true,
        description:
          "Enormous satirical sculptures are built across the city over months, then burned in a single night of fireworks.",
        expatTip:
          "The daily 2pm mascletà is a percussive firework display you feel in your chest. Bring ear protection for children and expect very limited sleep in the centre.",
      },
      {
        id: "la-tomatina",
        name: "La Tomatina",
        category: "Festival",
        startDate: "2026-08-26",
        dateLabel: "Wednesday 26 August 2026",
        timeLabel: "11am–1pm",
        city: "Buñol, Valencia",
        venue: "Plaza del Pueblo",
        address: "Plaza del Pueblo, 46360 Buñol, Valencia",
        priceLabel: "Ticket about €15 (entry is ticket-only and capped)",
        ticketUrl: "https://latomatina.info/",
        ticketLabel: "Buy La Tomatina tickets",
        description:
          "An hour-long tomato fight in a small Valencian town, capped at around 20,000 ticketed participants.",
        expatTip:
          "Wear clothes you will throw away, use sealed goggles, and leave your phone in a waterproof pouch. Trains from Valencia sell out; buy the combined coach transfer.",
      },
      {
        id: "semana-santa-sevilla",
        name: "Semana Santa in Seville",
        category: "Culture",
        startDate: "2027-03-21",
        endDate: "2027-03-28",
        dateLabel: "21–28 March 2027",
        timeLabel: "Processions from midday until sunrise",
        city: "Seville",
        venue: "Old town processional route to the Cathedral",
        address: "Av. de la Constitución, 41004 Sevilla",
        priceLabel: "Free on the street; Carrera Oficial seats ticketed",
        ticketUrl: "https://www.visitasevilla.es/en",
        ticketLabel: "Processions guide and seating",
        rsvp: true,
        description:
          "Holy Week processions in which brotherhoods carry elaborate floats through the old town day and night for a full week.",
        expatTip:
          "Download the official procession app — streets close without warning and crossing the route can add 40 minutes to any journey.",
      },
      {
        id: "primavera-sound",
        name: "Primavera Sound Barcelona",
        category: "Music",
        startDate: "2027-06-02",
        endDate: "2027-06-05",
        dateLabel: "2–5 June 2027",
        timeLabel: "Gates 4pm; music until 5am",
        city: "Barcelona",
        venue: "Parc del Fòrum",
        address: "Rambla Prim 2-4, 08019 Barcelona",
        priceLabel: "Full passes from about €245; day tickets from about €99",
        ticketUrl: "https://www.primaverasound.com/en",
        ticketLabel: "Buy Primavera Sound tickets",
        description:
          "A seafront festival known for booking new and legacy acts side by side, with sets running through the night.",
        expatTip:
          "Metro Line 4 runs all night during the festival. Early-bird passes released the previous autumn are typically 30–40% cheaper than gate prices.",
      },
    ],
  },

  // -------------------------------------------------------- NETHERLANDS ----
  {
    slug: "netherlands",
    name: "Netherlands",
    code: "nl",
    hasGuide: false,
    currency: "EUR",
    intro:
      "Dutch events are compact and superbly organised, and almost all of them are reachable by train and bicycle. King's Day in particular is the easiest single day in Europe for a newcomer to feel part of a city.",
    planning:
      "Buy an OV-chipkaart or use contactless on the trains; Amsterdam accommodation is expensive year-round and extreme around King's Day and Amsterdam Dance Event. Rain gear beats an umbrella in Dutch wind.",
    events: [
      {
        id: "kings-day",
        name: "King's Day (Koningsdag)",
        category: "Community",
        startDate: "2027-04-27",
        dateLabel: "Tuesday 27 April 2027",
        timeLabel: "From 6am (Amsterdam free market) to late evening",
        city: "Nationwide, centred on Amsterdam",
        venue: "Canals, streets and the citywide vrijmarkt",
        address: "Jordaan and Vondelpark, 1016 Amsterdam",
        priceLabel: "Free; boat parties ticketed",
        ticketUrl: "https://www.iamsterdam.com/en/whats-on/kings-day",
        ticketLabel: "King's Day guide and boat tickets",
        rsvp: true,
        description:
          "The King's birthday turns the whole country orange, with a nationwide open-air flea market and canal parties in Amsterdam.",
        expatTip:
          "The vrijmarkt is the one day a year anyone can sell second-hand goods without a permit — a practical way to clear out or furnish a flat cheaply.",
      },
      {
        id: "amsterdam-dance-event",
        name: "Amsterdam Dance Event (ADE)",
        category: "Music",
        startDate: "2026-10-21",
        endDate: "2026-10-25",
        dateLabel: "21–25 October 2026",
        timeLabel: "Conference 10am–6pm; club programme from 10pm",
        city: "Amsterdam",
        venue: "150+ venues citywide",
        address: "Various venues, Amsterdam",
        priceLabel: "Conference pass from about €395; single club events from €25",
        ticketUrl: "https://www.amsterdam-dance-event.nl/en/",
        ticketLabel: "Buy ADE tickets",
        description:
          "The world's largest electronic music conference and club festival, with daytime industry programming and 1,000+ nightly events.",
        expatTip:
          "You do not need the conference pass to enjoy ADE — individual club tickets are sold separately and many daytime talks are free.",
      },
      {
        id: "keukenhof",
        name: "Keukenhof Tulip Gardens",
        category: "Culture",
        startDate: "2027-03-18",
        endDate: "2027-05-16",
        dateLabel: "18 March – 16 May 2027",
        timeLabel: "8am–7:30pm daily",
        city: "Lisse",
        venue: "Keukenhof Park",
        address: "Stationsweg 166A, 2161 AM Lisse",
        priceLabel: "Adult entry about €20; combi bus tickets available",
        ticketUrl: "https://keukenhof.nl/en/",
        ticketLabel: "Book Keukenhof tickets",
        description:
          "Seven million bulbs planted across 32 hectares, open for eight weeks each spring.",
        expatTip:
          "Book a timed entry slot online and take the combi ticket with the shuttle bus from Schiphol — parking queues at weekends are severe.",
      },
      {
        id: "sinterklaas-intocht",
        name: "Sinterklaas Arrival (Intocht)",
        category: "Community",
        startDate: "2026-11-14",
        dateLabel: "Saturday 14 November 2026",
        timeLabel: "Late morning, roughly 11am–1pm",
        city: "Rotating host city, plus every town",
        venue: "Harbour arrival and parade route",
        address: "Announced by the host municipality each autumn",
        priceLabel: "Free",
        ticketUrl: "https://www.nederlandwereldwijd.nl/",
        ticketLabel: "Local Sinterklaas listings",
        rsvp: true,
        description:
          "Sinterklaas arrives by steamboat to a nationally televised welcome, starting three weeks of gift-giving that culminates on 5 December.",
        expatTip:
          "If you have children in a Dutch school, pakjesavond on 5 December matters far more locally than Christmas Day — budget and diary accordingly.",
      },
    ],
  },

  // -------------------------------------------------------- SWITZERLAND ----
  {
    slug: "switzerland",
    name: "Switzerland",
    code: "ch",
    hasGuide: false,
    currency: "CHF",
    intro:
      "Switzerland runs a small number of world-class events with immaculate logistics and high prices. Public transport connects nearly all of them, and punctuality is genuinely expected.",
    planning:
      "A Half Fare Card or saver-day pass pays back quickly if you attend more than one event. Book alpine accommodation months ahead, and note that many Swiss events are cantonal holidays in their host region only.",
    events: [
      {
        id: "montreux-jazz",
        name: "Montreux Jazz Festival",
        category: "Music",
        startDate: "2027-07-02",
        endDate: "2027-07-17",
        dateLabel: "2–17 July 2027",
        timeLabel: "Free stages from 4pm; paid concerts from 8pm",
        city: "Montreux",
        venue: "Montreux Music & Convention Centre, Lake Geneva",
        address: "Av. Claude Nobs 5, 1820 Montreux",
        priceLabel: "Many free lakeside stages; paid concerts from about CHF 90",
        ticketUrl: "https://www.montreuxjazzfestival.com/en/",
        ticketLabel: "Buy Montreux Jazz tickets",
        description:
          "Two weeks of concerts on the shore of Lake Geneva, spanning jazz, soul, rock and electronic programming.",
        expatTip:
          "Roughly half the festival is free on the lakeside stages. Stay in Lausanne or Vevey and take the regional train, which runs late during the festival.",
      },
      {
        id: "fasnacht-basel",
        name: "Basler Fasnacht",
        category: "Festival",
        startDate: "2027-02-15",
        endDate: "2027-02-17",
        dateLabel: "15–17 February 2027",
        timeLabel: "Morgestraich begins at 4am sharp",
        city: "Basel",
        venue: "Old town, citywide",
        address: "Marktplatz, 4001 Basel",
        priceLabel: "Free; Blaggedde badge about CHF 10 supports the cliques",
        ticketUrl: "https://www.fasnachts-comite.ch/",
        ticketLabel: "Fasnacht programme",
        rsvp: true,
        description:
          "A UNESCO-listed carnival that begins with the entire city's lights switched off at 4am and thousands of lanterns processing through the dark.",
        expatTip:
          "Buy the Blaggedde badge — it funds the participating groups and is what locals wear. Morgestraich is the one moment to set an alarm for.",
      },
      {
        id: "lauberhorn",
        name: "Lauberhorn Ski World Cup",
        category: "Sport",
        startDate: "2027-01-15",
        endDate: "2027-01-17",
        dateLabel: "15–17 January 2027",
        timeLabel: "Races from 12:30pm",
        city: "Wengen",
        venue: "Lauberhorn downhill course",
        address: "3823 Wengen, Bernese Oberland",
        priceLabel: "Standing tickets from about CHF 40",
        ticketUrl: "https://www.lauberhorn.ch/en/",
        ticketLabel: "Buy Lauberhorn tickets",
        description:
          "The longest downhill race on the World Cup circuit, run beneath the Eiger with a huge open-air crowd.",
        expatTip:
          "Wengen is car-free — you arrive by cogwheel train from Lauterbrunnen. Buy the combined race and transport ticket and start early on race day.",
      },
      {
        id: "sechselaeuten",
        name: "Sechseläuten",
        category: "Community",
        startDate: "2027-04-19",
        dateLabel: "Monday 19 April 2027",
        timeLabel: "Guild parade from 3pm; Böögg burns at 6pm",
        city: "Zurich",
        venue: "Sechseläutenplatz",
        address: "Sechseläutenplatz, 8001 Zürich",
        priceLabel: "Free; grandstand seats ticketed",
        ticketUrl: "https://www.sechselaeuten.ch/",
        ticketLabel: "Parade info and grandstand seats",
        rsvp: true,
        description:
          "Zurich's spring festival, where a snowman effigy is burned at 6pm and the time it takes his head to explode supposedly predicts the summer.",
        expatTip:
          "It is a half-day public holiday in the city of Zurich only. Many local employers close from midday — confirm with HR rather than assuming.",
      },
    ],
  },

  // ------------------------------------------------------------ IRELAND ----
  {
    slug: "ireland",
    name: "Ireland",
    code: "ie",
    hasGuide: false,
    currency: "EUR",
    intro:
      "Ireland's calendar is anchored by St Patrick's Day and a strong summer of traditional music and literary festivals. Dublin dominates, but the best-value events are consistently outside the capital.",
    planning:
      "Dublin hotel prices spike hard around St Patrick's weekend and rugby internationals — book by January. Intercity trains and Bus Éireann cover most festival towns, and a Leap Card saves meaningfully on Dublin transport.",
    events: [
      {
        id: "st-patricks-festival",
        name: "St Patrick's Festival",
        category: "Festival",
        startDate: "2027-03-15",
        endDate: "2027-03-17",
        dateLabel: "15–17 March 2027",
        timeLabel: "Parade from 12pm on 17 March",
        city: "Dublin",
        venue: "Parade route from Parnell Square to St Patrick's Cathedral",
        address: "O'Connell St Upper, Dublin 1",
        priceLabel: "Free to watch; grandstand seats from about €75",
        ticketUrl: "https://stpatricksfestival.ie/",
        ticketLabel: "Festival programme and grandstand tickets",
        rsvp: true,
        description:
          "Ireland's national day, with a large parade through central Dublin plus several days of music, film and family programming.",
        expatTip:
          "Grandstand seats sell out in January. Smaller town parades — Dingle, Westport, Killarney — are more relaxed and easier with children.",
      },
      {
        id: "fleadh-cheoil",
        name: "Fleadh Cheoil na hÉireann",
        category: "Music",
        startDate: "2026-08-02",
        endDate: "2026-08-09",
        dateLabel: "2–9 August 2026",
        timeLabel: "Sessions from midday to late night",
        city: "Host town announced annually",
        venue: "Streets, pubs and competition halls",
        address: "Host town centre — confirmed by Comhaltas each spring",
        priceLabel: "Street sessions free; concert tickets from about €20",
        ticketUrl: "https://comhaltas.ie/",
        ticketLabel: "Fleadh listings and tickets",
        description:
          "The world's largest festival of Irish traditional music, taking over an entire host town for a week of competitions and street sessions.",
        expatTip:
          "Accommodation in the host town disappears within weeks of the announcement. Nearby towns plus a bus is the realistic plan.",
      },
      {
        id: "bloomsday",
        name: "Bloomsday",
        category: "Culture",
        startDate: "2027-06-16",
        dateLabel: "Wednesday 16 June 2027",
        timeLabel: "Events from 8am to evening",
        city: "Dublin",
        venue: "James Joyce Centre & citywide readings",
        address: "35 N Great George's St, Dublin 1",
        priceLabel: "Many free events; walking tours from about €20",
        ticketUrl: "https://jamesjoyce.ie/",
        ticketLabel: "Bloomsday programme and tickets",
        rsvp: true,
        description:
          "A city-wide celebration of Ulysses, retracing Leopold Bloom's route through Dublin with readings, costume and Edwardian breakfasts.",
        expatTip:
          "You do not need to have read the novel. The free street performances around Davy Byrnes and Sandycove are the easiest entry point.",
      },
      {
        id: "galway-arts",
        name: "Galway International Arts Festival",
        category: "Culture",
        startDate: "2027-07-12",
        endDate: "2027-07-25",
        dateLabel: "12–25 July 2027",
        timeLabel: "Daytime exhibitions; performances from 6pm",
        city: "Galway",
        venue: "Big Top, Fisheries Field and citywide venues",
        address: "Fisheries Field, Galway H91",
        priceLabel: "Free street programme; ticketed shows from about €25",
        ticketUrl: "https://www.giaf.ie/",
        ticketLabel: "Buy festival tickets",
        description:
          "Two weeks of theatre, visual art and Big Top concerts that fill the western city each July.",
        expatTip:
          "Galway is small and the festival dominates it — book accommodation by March or commute from Athenry on the local train.",
      },
    ],
  },

  // -------------------------------------------------------- NEW ZEALAND ----
  {
    slug: "new-zealand",
    name: "New Zealand",
    code: "nz",
    hasGuide: false,
    currency: "NZD",
    intro:
      "New Zealand's events are outdoors, seasonal and community-run. Waitangi Day and Matariki carry real civic weight, and understanding both goes a long way toward settling in properly.",
    planning:
      "Domestic flights between the islands need booking well ahead over summer, and many events happen in small towns where accommodation is limited. Sun protection is not optional — UV levels are among the highest in the world.",
    events: [
      {
        id: "waitangi-day",
        name: "Waitangi Day",
        category: "Community",
        startDate: "2027-02-06",
        dateLabel: "Saturday 6 February 2027",
        timeLabel: "Dawn ceremony from 5am; events until 5pm",
        city: "Waitangi, Bay of Islands",
        venue: "Waitangi Treaty Grounds",
        address: "1 Tau Henare Dr, Paihia 0293",
        priceLabel: "Free on Waitangi Day; standard entry applies otherwise",
        ticketUrl: "https://www.waitangi.org.nz/",
        ticketLabel: "Waitangi Day programme",
        rsvp: true,
        description:
          "The national day marking the 1840 signing of the Treaty of Waitangi, with a dawn ceremony, waka launches and cultural performance.",
        expatTip:
          "Entry to the Treaty Grounds is free for everyone on the day. If you are new to Aotearoa, the guided history tour is the clearest introduction to the Treaty you will get.",
      },
      {
        id: "matariki",
        name: "Matariki (Māori New Year)",
        category: "Community",
        startDate: "2027-06-25",
        dateLabel: "Friday 25 June 2027 (public holiday)",
        timeLabel: "Dawn hautapu ceremonies; evening events citywide",
        city: "Nationwide",
        venue: "Community and iwi-hosted events",
        address: "Auckland, Wellington, Christchurch and regional centres",
        priceLabel: "Mostly free",
        ticketUrl: "https://www.matariki.net.nz/",
        ticketLabel: "Find Matariki events",
        rsvp: true,
        description:
          "New Zealand's newest public holiday, marking the rising of the Matariki star cluster with remembrance, feasting and looking ahead to the year.",
        expatTip:
          "The date moves each year with the lunar calendar. Council-run events are free, family-friendly and the most welcoming introduction to te ao Māori.",
      },
      {
        id: "wow-awards",
        name: "World of WearableArt (WOW)",
        category: "Culture",
        startDate: "2026-09-24",
        endDate: "2026-10-11",
        dateLabel: "24 September – 11 October 2026",
        timeLabel: "Evening shows from 7:30pm; matinees available",
        city: "Wellington",
        venue: "TSB Arena",
        address: "4 Queens Wharf, Wellington 6011",
        priceLabel: "Tickets from about NZ$95",
        ticketUrl: "https://www.worldofwearableart.com/",
        ticketLabel: "Buy WOW tickets",
        description:
          "A theatrical design competition where garments become stage spectacle — New Zealand's most distinctive cultural export.",
        expatTip:
          "Tickets go on sale in autumn and the strongest nights sell out first. Wellington hotel prices rise across the run.",
      },
      {
        id: "queenstown-winter-festival",
        name: "Queenstown Winter Festival",
        category: "Festival",
        startDate: "2027-06-17",
        endDate: "2027-06-20",
        dateLabel: "17–20 June 2027",
        timeLabel: "Street events from 10am; concerts in the evening",
        city: "Queenstown",
        venue: "Lakefront and Coronet Peak",
        address: "Earnslaw Park, Queenstown 9300",
        priceLabel: "Most street events free; ticketed parties vary",
        ticketUrl: "https://www.winterfestival.co.nz/",
        ticketLabel: "Winter Festival programme",
        rsvp: true,
        description:
          "The southern hemisphere's biggest winter party: a street parade, mountain races and four days of lakefront events opening the ski season.",
        expatTip:
          "Queenstown accommodation triples in price for the festival weekend. Frankton is cheaper with a ten-minute bus into town.",
      },
    ],
  },

  // -------------------------------------------------------------- JAPAN ----
  {
    slug: "japan",
    name: "Japan",
    code: "jp",
    hasGuide: false,
    currency: "JPY",
    intro:
      "Japan's festival calendar is dense, precise and heavily seasonal. Hanami in spring and the summer matsuri are the two moments when even the largest cities feel communal.",
    planning:
      "Avoid Golden Week (late April to early May) and Obon (mid-August) for travel — domestic transport and hotels are booked out and priced at peak. Reserve shinkansen seats through the official app, and carry some cash for festival stalls.",
    events: [
      {
        id: "hanami",
        name: "Cherry Blossom Season (Hanami)",
        category: "Culture",
        startDate: "2027-03-25",
        endDate: "2027-04-10",
        dateLabel: "Late March – early April 2027 (varies by region)",
        timeLabel: "Daylight hours; many parks light up until 9pm",
        city: "Nationwide (Tokyo, Kyoto, Osaka)",
        venue: "Ueno Park, Meguro River, Maruyama Park",
        address: "Ueno Park, Taito City, Tokyo 110-0007",
        priceLabel: "Free in public parks",
        ticketUrl: "https://www.japan.travel/en/",
        ticketLabel: "Bloom forecasts and park guides",
        rsvp: true,
        description:
          "Two weeks of blossom viewing with picnics under the trees, moving north across the country as the front advances.",
        expatTip:
          "Follow the official bloom forecast, which updates weekly from January. Popular Tokyo parks require someone to hold a spot from early morning.",
      },
      {
        id: "gion-matsuri",
        name: "Gion Matsuri",
        category: "Festival",
        startDate: "2027-07-01",
        endDate: "2027-07-31",
        dateLabel: "Throughout July 2027; main parade 17 July",
        timeLabel: "Yoiyama evenings 6pm–11pm; parade from 9am",
        city: "Kyoto",
        venue: "Shijo-Karasuma district",
        address: "Shijo St, Shimogyo Ward, Kyoto 600-8008",
        priceLabel: "Free on the street; paid grandstand seats for the parade",
        ticketUrl: "https://www.kyoto.travel/en/",
        ticketLabel: "Gion Matsuri guide and seats",
        rsvp: true,
        description:
          "Kyoto's thousand-year-old festival, with enormous wooden floats hauled through the city and three evenings of street celebration beforehand.",
        expatTip:
          "The yoiyama evenings before the parade are the atmospheric part — streets close to cars, food stalls open and machiya houses display heirlooms.",
      },
      {
        id: "sapporo-snow-festival",
        name: "Sapporo Snow Festival",
        category: "Festival",
        startDate: "2027-02-04",
        endDate: "2027-02-11",
        dateLabel: "4–11 February 2027",
        timeLabel: "Sites 9am–10pm; illuminations after dark",
        city: "Sapporo",
        venue: "Odori Park, Susukino and Tsudome",
        address: "Odori Park, Chuo Ward, Sapporo, Hokkaido 060-0042",
        priceLabel: "Free entry",
        ticketUrl: "https://www.snowfes.com/english/",
        ticketLabel: "Snow Festival site guide",
        rsvp: true,
        description:
          "Monumental snow and ice sculptures across three sites in Hokkaido's capital, drawing around two million visitors a week.",
        expatTip:
          "Book Sapporo accommodation six months out. Proper insulated boots with grip matter more than a heavier coat.",
      },
      {
        id: "sumida-fireworks",
        name: "Sumida River Fireworks Festival",
        category: "Community",
        startDate: "2027-07-31",
        dateLabel: "Saturday 31 July 2027",
        timeLabel: "7pm–8:30pm",
        city: "Tokyo",
        venue: "Sumida River, between Sakurabashi and Kototoibashi",
        address: "Sumida River, Taito City, Tokyo",
        priceLabel: "Free on public banks; paid viewing seats via ballot",
        ticketUrl: "https://www.sumidagawa-hanabi.com/",
        ticketLabel: "Fireworks info and seat ballot",
        rsvp: true,
        description:
          "Tokyo's oldest and largest fireworks display, with around 20,000 shells launched over the river in ninety minutes.",
        expatTip:
          "Nearly a million people attend. Watching from a rooftop bar in Asakusa or a riverside park further downstream is far more comfortable than the main banks.",
      },
    ],
  },

  // -------------------------------------------------------- SOUTH KOREA ----
  {
    slug: "south-korea",
    name: "South Korea",
    code: "kr",
    hasGuide: false,
    currency: "KRW",
    intro:
      "Korea's event calendar mixes centuries-old festivals with an aggressively modern music and gaming industry. Seoul dominates, but the regional festivals are where prices drop and English-speaking crowds thin out.",
    planning:
      "KTX tickets and Chuseok travel sell out early; the holiday itself shuts much of Seoul's commerce for several days. A T-money card covers subway, bus and many convenience purchases nationwide.",
    events: [
      {
        id: "seoul-lantern-festival",
        name: "Seoul Lantern Festival",
        category: "Culture",
        startDate: "2026-11-13",
        endDate: "2026-11-29",
        dateLabel: "13–29 November 2026",
        timeLabel: "Illuminated 5pm–11pm nightly",
        city: "Seoul",
        venue: "Cheonggyecheon Stream & Gwanghwamun Plaza",
        address: "Cheonggyecheon-ro, Jung-gu, Seoul",
        priceLabel: "Free",
        ticketUrl: "https://english.visitseoul.net/",
        ticketLabel: "Festival details and access",
        rsvp: true,
        description:
          "Hundreds of illuminated lanterns floated along the restored stream through central Seoul for just over two weeks.",
        expatTip:
          "Weeknights before 7pm are walkable; weekends become a one-directional crowd. Combine it with the Gwanghwamun night market.",
      },
      {
        id: "boryeong-mud-festival",
        name: "Boryeong Mud Festival",
        category: "Festival",
        startDate: "2027-07-16",
        endDate: "2027-07-25",
        dateLabel: "16–25 July 2027",
        timeLabel: "10am–8pm; concerts in the evening",
        city: "Boryeong",
        venue: "Daecheon Beach",
        address: "Daecheon Beach, Boryeong-si, Chungcheongnam-do",
        priceLabel: "Zone entry about ₩10,000–15,000",
        ticketUrl: "https://english.visitkorea.or.kr/",
        ticketLabel: "Official Korea tourism guide",
        description:
          "Mud pools, slides and wrestling on a west-coast beach, with evening concerts and fireworks.",
        expatTip:
          "Express buses from Seoul take about two and a half hours. Bring a change of clothes and a dry bag — showers on site have long queues.",
      },
      {
        id: "chuseok",
        name: "Chuseok (Korean Harvest Festival)",
        category: "Community",
        startDate: "2026-09-24",
        endDate: "2026-09-26",
        dateLabel: "24–26 September 2026",
        timeLabel: "Family observances in the morning; palace events all day",
        city: "Nationwide",
        venue: "Gyeongbokgung Palace and folk villages",
        address: "161 Sajik-ro, Jongno-gu, Seoul",
        priceLabel: "Free palace entry in hanbok; most events free",
        ticketUrl: "https://english.visitkorea.or.kr/",
        ticketLabel: "Chuseok programme and openings",
        rsvp: true,
        description:
          "Korea's biggest holiday, when families return to hometowns for ancestral rites and the palaces host free traditional programming.",
        expatTip:
          "Much of Seoul closes and intercity travel is gridlocked. If you are staying in the city, palace entry is free while wearing hanbok, which rents for about ₩15,000.",
      },
      {
        id: "busan-film-festival",
        name: "Busan International Film Festival",
        category: "Culture",
        startDate: "2026-10-01",
        endDate: "2026-10-10",
        dateLabel: "1–10 October 2026",
        timeLabel: "Screenings 10am–11pm",
        city: "Busan",
        venue: "Busan Cinema Center",
        address: "120 Suyeonggangbyeon-daero, Haeundae-gu, Busan",
        priceLabel: "Tickets about ₩8,000 per screening",
        ticketUrl: "https://www.biff.kr/eng/",
        ticketLabel: "Buy BIFF tickets",
        description:
          "Asia's leading film festival, with a strong Korean and pan-Asian programme and outdoor screenings at Haeundae.",
        expatTip:
          "Tickets are cheap but open online at a fixed time and disappear within minutes for headline films. Register an account before the on-sale date.",
      },
    ],
  },

  // ---------------------------------------------------------- SINGAPORE ----
  {
    slug: "singapore",
    name: "Singapore",
    code: "sg",
    hasGuide: false,
    currency: "SGD",
    intro:
      "Singapore's calendar reflects its four main communities plus a heavy business-events programme. Almost everything is indoors, air-conditioned and immaculately signposted, which makes it one of the easiest places to attend events as a newcomer.",
    planning:
      "Hotel rates spike during the Grand Prix and major conferences. Public transport reaches every venue; buy an EZ-Link card or tap a contactless card. Outdoor evening events still mean high humidity — dress accordingly.",
    events: [
      {
        id: "singapore-grand-prix",
        name: "Formula 1 Singapore Grand Prix",
        category: "Sport",
        startDate: "2026-10-02",
        endDate: "2026-10-04",
        dateLabel: "2–4 October 2026",
        timeLabel: "Gates 3pm; race starts 8pm Sunday",
        city: "Singapore",
        venue: "Marina Bay Street Circuit",
        address: "1 Republic Blvd, Singapore 038975",
        priceLabel: "Three-day walkabout from about S$298; grandstands considerably higher",
        ticketUrl: "https://www.singaporegp.sg/",
        ticketLabel: "Buy Singapore GP tickets",
        description:
          "The original Formula 1 night race, run on closed public roads around Marina Bay with concerts included in the ticket.",
        expatTip:
          "The walkabout ticket includes the concert lineup, which is often the better value. Road closures start days ahead — check the traffic advisory if you live in the CBD.",
      },
      {
        id: "chinese-new-year-sg",
        name: "Chinese New Year & Chingay Parade",
        category: "Community",
        startDate: "2027-02-06",
        endDate: "2027-02-07",
        dateLabel: "6–7 February 2027 (CNY 6 February)",
        timeLabel: "Chingay from 8pm; Chinatown light-up all evening",
        city: "Singapore",
        venue: "F1 Pit Building & Chinatown",
        address: "1 Republic Blvd, Singapore 038975",
        priceLabel: "Chinatown free; Chingay tickets from about S$30",
        ticketUrl: "https://www.chingay.gov.sg/",
        ticketLabel: "Buy Chingay tickets",
        description:
          "Two weeks of celebration culminating in Chingay, Asia's largest street parade, with floats, lion dance and mass performance.",
        expatTip:
          "Most small businesses close for the first two days of the new year. Chinatown's evening market in the fortnight beforehand is free and the best photography.",
      },
      {
        id: "deepavali-sg",
        name: "Deepavali in Little India",
        category: "Culture",
        startDate: "2026-11-08",
        dateLabel: "Sunday 8 November 2026 (light-up from October)",
        timeLabel: "Street light-up from 7pm nightly",
        city: "Singapore",
        venue: "Serangoon Road, Little India",
        address: "Serangoon Rd, Singapore 218075",
        priceLabel: "Free",
        ticketUrl: "https://www.visitsingapore.com/",
        ticketLabel: "Deepavali events guide",
        rsvp: true,
        description:
          "Serangoon Road is lit end to end for a month, with a festival village, street bazaar and temple ceremonies.",
        expatTip:
          "The bazaar at Campbell Lane is the best place in the city to buy inexpensive gifts and sweets. Go on a weeknight — weekends are shoulder-to-shoulder.",
      },
      {
        id: "singapore-night-festival",
        name: "Singapore Night Festival",
        category: "Festival",
        startDate: "2027-08-20",
        endDate: "2027-08-28",
        dateLabel: "20–28 August 2027, Friday and Saturday nights",
        timeLabel: "7:30pm–midnight",
        city: "Singapore",
        venue: "Bras Basah.Bugis precinct",
        address: "Armenian St, Singapore 179932",
        priceLabel: "Free; some ticketed performances",
        ticketUrl: "https://www.nightfestival.sg/",
        ticketLabel: "Night Festival programme",
        rsvp: true,
        description:
          "Light projections on the National Museum façade, late-opening galleries and street performance across the arts district.",
        expatTip:
          "Museums stay open late and free during the festival — the single cheapest cultural weekend in Singapore's year.",
      },
    ],
  },

  // ----------------------------------------------------------------UAE ----
  {
    slug: "uae",
    name: "United Arab Emirates",
    code: "ae",
    hasGuide: false,
    currency: "AED",
    intro:
      "The UAE's event season runs from October to April, when outdoor temperatures are comfortable. Shopping festivals, sport and large business conferences dominate, and almost all are professionally ticketed online.",
    planning:
      "Book flights and hotels well ahead for GITEX and the Grand Prix. During Ramadan, daytime hours change and eating in public is restricted; evenings become the main social period. Modest dress is expected at cultural venues.",
    events: [
      {
        id: "dubai-shopping-festival",
        name: "Dubai Shopping Festival",
        category: "Festival",
        startDate: "2026-12-06",
        endDate: "2027-01-12",
        dateLabel: "6 December 2026 – 12 January 2027",
        timeLabel: "Mall hours 10am–midnight; nightly fireworks",
        city: "Dubai",
        venue: "Malls, Global Village and Al Seef",
        address: "Downtown Dubai, Dubai",
        priceLabel: "Free entry; Global Village entry about AED 25",
        ticketUrl: "https://u.ae/en",
        ticketLabel: "DSF programme and tickets",
        rsvp: true,
        description:
          "Five weeks of citywide sales, concerts, fireworks and outdoor markets across Dubai.",
        expatTip:
          "Discounts are genuine on electronics and gold. Global Village is the best-value evening out in the city for families and closes for the summer.",
      },
      {
        id: "gitex-global",
        name: "GITEX Global",
        category: "Tech",
        startDate: "2026-10-12",
        endDate: "2026-10-16",
        dateLabel: "12–16 October 2026",
        timeLabel: "10am–6pm daily",
        city: "Dubai",
        venue: "Dubai World Trade Centre",
        address: "Sheikh Zayed Rd, Dubai",
        priceLabel: "Trade passes from about AED 400; startup passes discounted",
        ticketUrl: "https://www.gitex.com/",
        ticketLabel: "Register for GITEX",
        description:
          "One of the world's largest technology exhibitions, with a substantial startup track in the co-located Expand North Star programme.",
        expatTip:
          "If you are job-hunting in the Gulf tech sector, this is the single densest week of recruiter contact in the region. Book hotels in Business Bay early.",
      },
      {
        id: "abu-dhabi-gp",
        name: "Abu Dhabi Grand Prix",
        category: "Sport",
        startDate: "2026-12-04",
        endDate: "2026-12-06",
        dateLabel: "4–6 December 2026",
        timeLabel: "Gates from midday; race at 5pm Sunday",
        city: "Abu Dhabi",
        venue: "Yas Marina Circuit",
        address: "Yas Island, Abu Dhabi",
        priceLabel: "Three-day tickets from about AED 1,595",
        ticketUrl: "https://www.yasmarinacircuit.com/",
        ticketLabel: "Buy Abu Dhabi GP tickets",
        description:
          "The traditional Formula 1 season finale, a twilight race with major concerts on each of the three nights.",
        expatTip:
          "Tickets include the after-race concerts and Yas Island park access on some tiers — read the inclusions before comparing prices.",
      },
      {
        id: "eid-al-fitr-uae",
        name: "Eid al-Fitr Celebrations",
        category: "Community",
        startDate: "2027-03-09",
        endDate: "2027-03-12",
        dateLabel: "Approximately 9–12 March 2027 (moon-dependent)",
        timeLabel: "Prayers at dawn; family events all day",
        city: "Nationwide",
        venue: "Mosques, corniches and public parks",
        address: "Sheikh Zayed Grand Mosque, Abu Dhabi",
        priceLabel: "Free public events",
        ticketUrl: "https://u.ae/en/information-and-services/public-holidays-and-religious-affairs",
        ticketLabel: "Official holiday dates",
        rsvp: true,
        description:
          "The end of Ramadan, marked by dawn prayers, family gatherings, public fireworks and several days of national holiday.",
        expatTip:
          "Exact dates are confirmed only days ahead by moon sighting, so hold flights flexibly. Restaurants and malls are exceptionally busy in the evenings.",
      },
    ],
  },

  // ------------------------------------------------------------- BRAZIL ----
  {
    slug: "brazil",
    name: "Brazil",
    code: "br",
    hasGuide: false,
    currency: "BRL",
    intro:
      "Brazilian events are enormous, regional and mostly free at street level. Carnival is the obvious anchor, but each region — Bahia, Pernambuco, Rio, São Paulo — celebrates it in a genuinely different way.",
    planning:
      "Book domestic flights and accommodation for Carnival four to six months ahead. Carry a local card or Pix-enabled account rather than cash, keep valuables minimal in crowds, and expect events to start much later in the evening than in Europe.",
    events: [
      {
        id: "carnival-rio",
        name: "Rio Carnival",
        category: "Festival",
        startDate: "2027-02-05",
        endDate: "2027-02-10",
        dateLabel: "5–10 February 2027",
        timeLabel: "Street blocos from 7am; Sambadrome parades from 9pm",
        city: "Rio de Janeiro",
        venue: "Sambadrome Marquês de Sapucaí & citywide blocos",
        address: "R. Marquês de Sapucaí, Santo Cristo, Rio de Janeiro",
        priceLabel: "Blocos free; Sambadrome seats from about R$250",
        ticketUrl: "https://www.rio.rj.gov.br/",
        ticketLabel: "Sambadrome tickets and bloco listings",
        rsvp: true,
        description:
          "The world's largest carnival: samba school parades in the Sambadrome plus hundreds of free street blocos across the city.",
        expatTip:
          "The free blocos are the real Carnival for most residents. Buy Sambadrome tickets only through official channels — resale fraud spikes every year.",
      },
      {
        id: "salvador-carnival",
        name: "Salvador Carnival",
        category: "Festival",
        startDate: "2027-02-05",
        endDate: "2027-02-10",
        dateLabel: "5–10 February 2027",
        timeLabel: "Circuits run 4pm until dawn",
        city: "Salvador, Bahia",
        venue: "Barra-Ondina and Campo Grande circuits",
        address: "Av. Oceânica, Barra, Salvador, BA",
        priceLabel: "Free in the pipoca crowd; abadá blocks from about R$400",
        ticketUrl: "https://www.salvador.ba.gov.br/",
        ticketLabel: "Circuits and abadá info",
        rsvp: true,
        description:
          "The largest street party on earth by participation, built around trio elétrico sound trucks moving through the city for six days.",
        expatTip:
          "Buying an abadá gets you inside a roped-off block with security and toilets. Free 'pipoca' is more authentic but demands real crowd awareness.",
      },
      {
        id: "sao-joao-brazil",
        name: "Festa Junina / São João",
        category: "Community",
        startDate: "2027-06-01",
        endDate: "2027-06-30",
        dateLabel: "Throughout June 2027; peak 23–24 June",
        timeLabel: "Evenings from 7pm",
        city: "Campina Grande & Caruaru (Northeast)",
        venue: "Parque do Povo and town squares",
        address: "Parque do Povo, Campina Grande, PB",
        priceLabel: "Mostly free; some ticketed concerts",
        ticketUrl: "https://www.gov.br/turismo/pt-br",
        ticketLabel: "São João programme",
        rsvp: true,
        description:
          "A month of forró music, quadrilha dancing, bonfires and regional food across the Brazilian northeast.",
        expatTip:
          "This is Brazil's second-biggest celebration and far less touristed than Carnival. Campina Grande calls itself the largest São João in the world for good reason.",
      },
      {
        id: "rock-in-rio",
        name: "Rock in Rio",
        category: "Music",
        startDate: "2027-09-17",
        endDate: "2027-09-26",
        dateLabel: "Two weekends, 17–26 September 2027",
        timeLabel: "Gates 2pm; music until 4am",
        city: "Rio de Janeiro",
        venue: "Parque Olímpico, Barra da Tijuca",
        address: "Av. Embaixador Abelardo Bueno 3401, Rio de Janeiro",
        priceLabel: "Day tickets from about R$625",
        ticketUrl: "https://rockinrio.com/rio/en/",
        ticketLabel: "Buy Rock in Rio tickets",
        description:
          "One of the largest music festivals in the world, staged across two weekends with multiple stages and a purpose-built site.",
        expatTip:
          "Tickets sell out in the pre-sale the previous year. Official shuttle buses from Barra and Recreio are the only sensible way in and out.",
      },
    ],
  },

  // ------------------------------------------------------------- MEXICO ----
  {
    slug: "mexico",
    name: "Mexico",
    code: "mx",
    hasGuide: false,
    currency: "MXN",
    intro:
      "Mexico's calendar is built on civic and religious tradition, and the biggest dates — Día de Muertos, Independence Day, Guelaguetza — are free, public and genuinely welcoming to newcomers.",
    planning:
      "Oaxaca and Mexico City fill completely for Día de Muertos; book by June. Domestic buses are comfortable and cheap for regional festivals, and altitude in Mexico City affects most new arrivals for the first few days.",
    events: [
      {
        id: "dia-de-muertos",
        name: "Día de Muertos",
        category: "Culture",
        startDate: "2026-10-31",
        endDate: "2026-11-02",
        dateLabel: "31 October – 2 November 2026",
        timeLabel: "Parades in the afternoon; cemetery vigils overnight",
        city: "Mexico City & Oaxaca",
        venue: "Zócalo, Paseo de la Reforma, Panteón San Miguel",
        address: "Plaza de la Constitución, Centro, Mexico City",
        priceLabel: "Free",
        ticketUrl: "https://www.gob.mx/sectur",
        ticketLabel: "Día de Muertos events guide",
        rsvp: true,
        description:
          "A UNESCO-listed remembrance of the dead: home altars, marigold paths, cemetery vigils and a large parade in the capital.",
        expatTip:
          "In Oaxaca, ask permission before photographing families at a graveside. Community comparsas in the villages are more meaningful than the city parade.",
      },
      {
        id: "guelaguetza",
        name: "Guelaguetza",
        category: "Culture",
        startDate: "2027-07-19",
        endDate: "2027-07-26",
        dateLabel: "19–26 July 2027",
        timeLabel: "Morning and afternoon performances from 10am",
        city: "Oaxaca",
        venue: "Auditorio Guelaguetza, Cerro del Fortín",
        address: "Cerro del Fortín, Oaxaca de Juárez, Oax.",
        priceLabel: "Free upper sections; reserved seats from about MX$1,000",
        ticketUrl: "https://www.oaxaca-mio.com/guelaguetza/",
        ticketLabel: "Guelaguetza tickets and programme",
        description:
          "Delegations from Oaxaca's eight regions perform traditional dance and music, giving away regional produce to the audience.",
        expatTip:
          "Free seating is genuinely available but requires queuing from early morning. Village-level Guelaguetzas in the weeks around it are quieter and free.",
      },
      {
        id: "grito-de-independencia",
        name: "Grito de Independencia",
        category: "Community",
        startDate: "2027-09-15",
        endDate: "2027-09-16",
        dateLabel: "15–16 September 2027",
        timeLabel: "El Grito at 11pm on the 15th; parade 11am on the 16th",
        city: "Mexico City",
        venue: "Zócalo",
        address: "Plaza de la Constitución, Centro, Mexico City",
        priceLabel: "Free",
        ticketUrl: "https://www.cdmx.gob.mx/",
        ticketLabel: "Official Grito programme",
        rsvp: true,
        description:
          "Independence Day, opening with the president's Grito from the National Palace balcony and a military parade the next morning.",
        expatTip:
          "The Zócalo has security screening and fills by early evening. Neighbourhood plazas hold their own Grito with much easier access.",
      },
      {
        id: "cervantino",
        name: "Festival Internacional Cervantino",
        category: "Culture",
        startDate: "2026-10-07",
        endDate: "2026-10-25",
        dateLabel: "7–25 October 2026",
        timeLabel: "Performances from midday to late evening",
        city: "Guanajuato",
        venue: "Teatro Juárez, Alhóndiga and city plazas",
        address: "Teatro Juárez, Sopeña s/n, Guanajuato, Gto.",
        priceLabel: "Many free street performances; ticketed shows from about MX$200",
        ticketUrl: "https://festivalcervantino.gob.mx/",
        ticketLabel: "Buy Cervantino tickets",
        description:
          "Latin America's largest arts festival, filling a colonial hill town with theatre, opera, dance and street performance for three weeks.",
        expatTip:
          "Guanajuato is small and hilly; book accommodation months ahead and expect a lot of walking on steep callejones.",
      },
    ],
  },

  // ------------------------------------------------------- SOUTH AFRICA ----
  {
    slug: "south-africa",
    name: "South Africa",
    code: "za",
    hasGuide: false,
    currency: "ZAR",
    intro:
      "South Africa's events combine strong arts programming with outdoor sport and a wine and food calendar that is world class for the price. Most run in the Cape summer between November and March.",
    planning:
      "Cape Town accommodation peaks from mid-December to mid-January. Ride-hailing is the standard way to move at night, load-shedding can affect smaller venues so check the schedule, and outdoor events need serious sun protection.",
    events: [
      {
        id: "cape-town-jazz",
        name: "Cape Town International Jazz Festival",
        category: "Music",
        startDate: "2027-03-26",
        endDate: "2027-03-27",
        dateLabel: "26–27 March 2027",
        timeLabel: "Doors 5pm; music until 2am",
        city: "Cape Town",
        venue: "Cape Town International Convention Centre",
        address: "1 Lower Long St, Cape Town 8001",
        priceLabel: "Weekend passes from about R1,300",
        ticketUrl: "https://www.capetownjazzfest.com/",
        ticketLabel: "Buy Jazz Festival tickets",
        description:
          "Africa's largest jazz gathering, with five stages and a free community concert in Greenmarket Square beforehand.",
        expatTip:
          "The free Greenmarket Square concert the day before is excellent and requires no ticket. Book accommodation in the CBD so you can walk home.",
      },
      {
        id: "cape-town-cycle-tour",
        name: "Cape Town Cycle Tour",
        category: "Sport",
        startDate: "2027-03-14",
        dateLabel: "Sunday 14 March 2027",
        timeLabel: "First group start 6:15am",
        city: "Cape Town",
        venue: "109 km route around the Cape Peninsula",
        address: "Start: Hertzog Blvd, Cape Town 8001",
        priceLabel: "Entry from about R800; free to spectate",
        ticketUrl: "https://www.capetowncycletour.com/",
        ticketLabel: "Enter the Cycle Tour",
        description:
          "The world's largest timed cycle race, with around 35,000 riders circling the peninsula past Chapman's Peak.",
        expatTip:
          "Entries open the previous September and the peninsula roads close for the whole morning — plan any Sunday travel around it whether or not you ride.",
      },
      {
        id: "national-arts-festival",
        name: "National Arts Festival",
        category: "Culture",
        startDate: "2027-06-24",
        endDate: "2027-07-04",
        dateLabel: "24 June – 4 July 2027",
        timeLabel: "Shows from 10am to midnight",
        city: "Makhanda (Grahamstown)",
        venue: "Venues across the town",
        address: "Monument Hill, Makhanda 6139, Eastern Cape",
        priceLabel: "Tickets typically R80–R250",
        ticketUrl: "https://nationalartsfestival.co.za/",
        ticketLabel: "Buy Festival tickets",
        description:
          "Africa's largest arts festival: eleven days of theatre, dance, jazz and a huge fringe in a small Eastern Cape university town.",
        expatTip:
          "Accommodation is mostly student digs and guesthouses and books out by April. Ticket prices are among the lowest of any major festival worldwide.",
      },
      {
        id: "afrikaburn",
        name: "AfrikaBurn",
        category: "Festival",
        startDate: "2027-04-26",
        endDate: "2027-05-02",
        dateLabel: "26 April – 2 May 2027",
        timeLabel: "Continuous; gate opens 9am on day one",
        city: "Tankwa Karoo",
        venue: "Quaggafontein, Tankwa Karoo",
        address: "Tankwa Karoo, Northern Cape",
        priceLabel: "Tiered tickets from about R1,600",
        ticketUrl: "https://www.afrikaburn.org/",
        ticketLabel: "Buy AfrikaBurn tickets",
        description:
          "A radically self-reliant desert gathering of art, performance and burn structures — nothing is sold on site.",
        expatTip:
          "You must bring all your own water, food, shelter and fuel; the nearest shop is hours away on gravel road. A high-clearance vehicle is strongly advised.",
      },
    ],
  },
];

export const COUNTRY_EVENTS: CountryEventsData[] = DATA.map((c) => ({
  ...c,
  events: [...c.events].sort((a, b) => a.startDate.localeCompare(b.startDate)),
}));

export function getCountryEvents(slug: string): CountryEventsData | undefined {
  return COUNTRY_EVENTS.find((c) => c.slug === slug);
}

export const EVENT_COUNTRY_SLUGS: string[] = COUNTRY_EVENTS.map((c) => c.slug);

export const TOTAL_EVENT_COUNT: number = COUNTRY_EVENTS.reduce(
  (n, c) => n + c.events.length,
  0
);
